/**
 * Every string on the visitor's surface.
 *
 * Separate file for the reason the panel's copy is: `.claude/design-context.md`
 * makes "legible at a glance to someone who has never seen it" a brand lock,
 * and a copy module can be walked by a test where copy scattered through
 * JSX cannot.
 *
 * The bar here is higher than legibility. A stranger who reads this
 * correctly and feels nothing has failed, so these are written to be
 * spoken rather than to be parsed.
 */
export const conversationCopy = {
  /** The speaker slot for the visitor's own lines. */
  you: "You",

  /**
   * The empty state, before anyone has said anything.
   *
   * Not "no messages yet". A visitor arriving at an empty room should be
   * told a character is coming, because the arrival is beat 0 and the
   * cold-start problem is what DEMO-SPEC says kills demos.
   */
  waiting: "Someone is already here.",

  /** One field, one action. */
  placeholder: "Say something",
  /** Once there is more than one person, say who is being spoken to. */
  placeholderTo: (name: string): string => `Say something to ${name}`,
  send: "Send",

  /**
   * Bringing someone into the room, in words rather than a plus sign.
   *
   * Beat 1 only happens if a stranger discovers they can do this, so the
   * invitation says what it does. The direction loop's other candidate put
   * the whole room in a sentence, which read better and made the state --
   * *who you are speaking to* -- much harder to see; this keeps that
   * treatment's contribution and not its cost.
   */
  bringSomeoneIn: "bring someone in",
  describePlaceholder: "Describe someone, in a sentence",
  describeCancel: "never mind",
  bringing: "bringing them in…",
  castFull: "The room is full.",

  /**
   * The one thing a visitor can do that is not talking, and it is still
   * talking: beat 1 is putting a new character in the room with a single
   * sentence, through the same field.
   */
  composerHint: "Describe someone to bring into the room",

  /**
   * The two blanks a visitor is allowed to see, in two registers.
   *
   * They are different kinds of fact and look different on purpose. A
   * missing line is a fact about the performance, so it is set as a
   * stage direction under the name of whoever's turn it was: a printed
   * play has had a convention for a present character who does not speak
   * for four centuries, and that convention is not an error dialogue.
   * Nothing arriving from the microphone is a fact about the visitor's
   * equipment, so it carries no name at all and is written plainly.
   * Attributing it to a character would make her answer for a dead mic.
   *
   * `noReply` says "the line", not "her line". Characters here are made
   * by a visitor out of one sentence and may be anyone; the name is
   * already in the slot beside this, so the possessive buys nothing and
   * can be wrong.
   *
   * Neither says "billed", though a `no_reply` turn is paid for in full.
   * A visitor holds no invoice and can do nothing with the fact. The
   * take log is where a developer reads it, in
   * `takeLogStreamCopy.emptyReason`.
   */
  blanks: {
    /** Set as a stage direction; its brackets are markup, not copy. */
    noReply: "the line doesn't come",
    /** The room, unattributed. A plain sentence, no brackets. */
    notHeard: "Nothing came through the microphone.",
  },

  states: {
    connecting: "Finding the room.",
    lost: "Lost the room. What is here is the last of it.",
    thinking: "…",
  },
} as const;
